import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnInit, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { finalize } from 'rxjs';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { EditorModule } from 'primeng/editor';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { TextareaModule } from 'primeng/textarea';
import { ToastModule } from 'primeng/toast';
import { PageDataPanelComponent } from '../../shared/page-layout/page-data-panel.component';
import { PageFormSectionComponent } from '../../shared/page-layout/page-form-section.component';
import { PageHeroComponent } from '../../shared/page-layout/page-hero.component';
import { PageShellComponent } from '../../shared/page-layout/page-shell.component';
import { SessionStorageService } from '../../services/session-storage.service';
import { ToolbarService } from '../../services/toolbar.service';
import { ContractsService } from '../contracts.service';
import { ContractDocumentPreview, ContractRevisionSection, ContractTemplate, GeneratedContractDocument } from '../models/contract-drafting.model';
import { PendingActionTracker } from '../../shared/state/pending-action-tracker';

@Component({
  selector: 'app-contract-document-workspace',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    ButtonModule,
    EditorModule,
    IftaLabelModule,
    InputGroupModule,
    InputGroupAddonModule,
    InputTextModule,
    SelectModule,
    TableModule,
    TagModule,
    TextareaModule,
    ToastModule,
    PageShellComponent,
    PageHeroComponent,
    PageFormSectionComponent,
    PageDataPanelComponent
  ],
  providers: [MessageService],
  templateUrl: './contract-document-workspace.component.html',
  styleUrls: ['./contract-document-workspace.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ContractDocumentWorkspaceComponent implements OnInit {
  readonly reportActions = new PendingActionTracker<string>();
  contractId!: number;
  revisionId!: number;
  companyId?: number;
  actorId?: number;

  preview = signal<ContractDocumentPreview | null>(null);
  sections = signal<ContractRevisionSection[]>([]);
  documents = signal<GeneratedContractDocument[]>([]);
  templates = signal<ContractTemplate[]>([]);
  selectedDocument = signal<GeneratedContractDocument | null>(null);
  activeTab = signal('sections');
  loading = signal(false);
  saving = signal(false);

  draftTitle = 'Generated Contract Draft';
  selectedTemplateId?: number;
  signedDocumentUrl = '';

  tabs = [
    { id: 'sections', label: 'Sections', icon: 'fa-solid fa-file-lines' },
    { id: 'preview', label: 'Preview', icon: 'fa-solid fa-eye' },
    { id: 'documents', label: 'Documents', icon: 'fa-solid fa-folder-open' },
    { id: 'signature', label: 'Signature', icon: 'fa-solid fa-signature' }
  ];

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private contractsService: ContractsService,
    private sessionStorageService: SessionStorageService,
    private toolbarService: ToolbarService,
    private messageService: MessageService
  ) {}

  ngOnInit() {
    this.contractId = Number(this.route.snapshot.paramMap.get('id'));
    this.revisionId = Number(this.route.snapshot.paramMap.get('revisionId'));
    this.route.queryParamMap.subscribe(queryParams => {
      this.activeTab.set(this.resolveTab(queryParams.get('tab')));
    });
    this.companyId = this.sessionStorageService.getCompany()?.id;
    this.actorId = this.toolbarService.customer()?.id;
    this.loadTemplates();
    this.loadWorkspace();
  }

  setTab(tabId: string) {
    const resolvedTab = this.resolveTab(tabId);
    this.activeTab.set(resolvedTab);
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { tab: resolvedTab },
      queryParamsHandling: 'merge',
      replaceUrl: true
    });
  }

  loadWorkspace() {
    this.loading.set(true);
    this.contractsService.getDocumentPreview(this.contractId, this.revisionId, this.companyId).subscribe({
      next: preview => {
        this.preview.set(preview);
        this.sections.set(preview.sections || []);
        this.documents.set(preview.generatedDocuments || []);
        this.selectedDocument.set(this.documents()[0] || null);
        this.loading.set(false);
      },
      error: () => {
        this.loading.set(false);
        this.messageService.add({ severity: 'error', summary: 'Load failed', detail: 'Could not load contract document workspace.' });
      }
    });
  }

  initializeSections() {
    this.contractsService.initializeRevisionSections(this.contractId, this.revisionId, this.companyId, this.actorId).subscribe(sections => {
      this.sections.set(sections);
      this.messageService.add({ severity: 'success', summary: 'Sections initialized', detail: 'Default contract sections are ready.' });
      this.loadWorkspace();
    });
  }

  saveSections() {
    this.saving.set(true);
    this.contractsService.saveRevisionSections(this.contractId, this.revisionId, this.sections()).subscribe({
      next: sections => {
        this.sections.set(sections);
        this.saving.set(false);
        this.messageService.add({ severity: 'success', summary: 'Saved', detail: 'Contract sections saved.' });
        this.loadWorkspace();
      },
      error: () => {
        this.saving.set(false);
        this.messageService.add({ severity: 'error', summary: 'Save failed', detail: 'Could not save contract sections.' });
      }
    });
  }

  generateDraft(actionKey = 'generate-draft') {
    if (!this.reportActions.begin(actionKey)) return;
    this.contractsService.generateContractDocument(this.contractId, this.revisionId, {
      companyId: this.companyId,
      templateId: this.selectedTemplateId,
      title: this.draftTitle,
      actorId: this.actorId
    }).pipe(finalize(() => this.reportActions.end(actionKey))).subscribe(document => {
      this.documents.set([document, ...this.documents()]);
      this.selectedDocument.set(document);
      this.setTab('documents');
      this.messageService.add({ severity: 'success', summary: 'Draft generated', detail: 'Generated contract draft is stored.' });
      this.loadWorkspace();
    });
  }

  approveDraft() {
    const document = this.selectedDocument();
    if (!document?.documentId) return;
    this.contractsService.approveGeneratedContractDocument(this.contractId, this.revisionId, document.documentId, this.actorId).subscribe(updated => {
      this.selectedDocument.set(updated);
      this.messageService.add({ severity: 'success', summary: 'Approved', detail: 'Generated draft approved.' });
      this.loadWorkspace();
    });
  }

  saveSignedUrl() {
    const document = this.selectedDocument();
    if (!document?.documentId || !this.signedDocumentUrl) return;
    this.contractsService.saveSignedDocumentUrl(this.contractId, this.revisionId, document.documentId, {
      signedDocumentUrl: this.signedDocumentUrl,
      actorId: this.actorId,
      title: 'Signed Agreement',
      documentType: 'SIGNED_AGREEMENT'
    }).subscribe(updated => {
      this.selectedDocument.set(updated);
      this.messageService.add({ severity: 'success', summary: 'Signed document saved', detail: 'Signed agreement URL was recorded.' });
      this.loadWorkspace();
    });
  }

  createPdfLink() {
    const document = this.selectedDocument();
    if (!document?.documentId) return;
    this.contractsService.createContractPdfLink(this.contractId, this.revisionId, document.documentId).subscribe(updated => {
      this.selectedDocument.set(updated);
      this.messageService.add({ severity: 'success', summary: 'PDF link created', detail: 'Generated PDF URL was stored.' });
      this.loadWorkspace();
    });
  }

  previewPdf() {
    const document = this.selectedDocument();
    if (!document?.documentId) {
      this.generateDraft('preview-pdf');
      return;
    }
    if (!this.reportActions.begin('preview-pdf')) return;
    this.contractsService.downloadContractPdf(this.contractId, this.revisionId, document.documentId).pipe(
      finalize(() => this.reportActions.end('preview-pdf'))
    ).subscribe(blob => {
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    });
  }

  selectDocument(document: GeneratedContractDocument) {
    this.selectedDocument.set(document);
    this.signedDocumentUrl = document.signedDocumentUrl || '';
  }

  private loadTemplates() {
    this.contractsService.getContractTemplates(this.companyId, true).subscribe(templates => this.templates.set(templates || []));
  }

  private resolveTab(tabId: string | null): string {
    return this.tabs.some(tab => tab.id === tabId) ? tabId! : 'sections';
  }
}
